import {gql} from '@apollo/client';
import stringify from 'json-stable-stringify';

import {assert, assertAndContinue} from 'Asserts';
import {PlateSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {validateItemsForParentStorable} from 'inventory/location-validation/ParentStorableValidationUtils';
import {
  GetContainerParentStorable,
  GetContainerParentStorableVariables,
} from 'notebook/integrations/samples/transfer-table-columns/__generated/GetContainerParentStorable';
import {
  GetPlateParentStorable,
  GetPlateParentStorableVariables,
} from 'notebook/integrations/samples/transfer-table-columns/__generated/GetPlateParentStorable';
import {extractContainerId} from 'notebook/integrations/samples/transfer-table-columns/extractContainerId';
import extractPlateId from 'notebook/integrations/samples/transfer-table-columns/extractPlateId';
import extractStorableId from 'notebook/integrations/samples/transfer-table-columns/extractStorableId';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {
  BoxColumnDefinition,
  ParentStorableColumnDefinition,
  PlateColumnDefinition,
  PositionColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomEval} from 'notebook/tables/constants/CustomEvalConstants';
import {
  CustomResolutionQueryType,
  InventoryTableGridPositionFromContainerQuery,
  InventoryTableParentStorableFromContainerQuery,
  InventoryTableParentStorableFromPlateQuery,
  InventoryValidateParentStorableCapacityQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {GridCoordinatesTypedValue, StorablesTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import APIUtil from 'util/APIUtil';
import lruMemoize from 'util/lruMemoize';

export const GET_CONTAINER_PARENT_STORABLE_QUERY = gql`
  query GetContainerParentStorable($containerIds: [StrictString]!) @skipAudit @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        storableSchema {
          ... on StorableSchemaInterface {
            id
          }
        }
        parentStorable {
          ... on StorableInterface {
            id
            name
            barcode {
              value
            }
          }
          ...InaccessibleInfoGraphQL
        }
        gridCell {
          ... on GridCellNode {
            columnIndex
            rowIndex
          }
          ...InaccessibleInfoGraphQL
        }
        contents {
          id
          containedItem {
            ... on RegistryItemInterface {
              id
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const GET_PLATE_PARENT_STORABLE_QUERY = gql`
  query GetPlateParentStorable($plateIds: [StrictString]!) @skipAudit @engComponent(name: INVENTORY) {
    plates(ids: $plateIds) {
      ... on PlateNode {
        id
        storableSchema {
          ... on PlateSchemaNode {
            id
            plateSchemaType
          }
        }
        parentStorable {
          ... on StorableInterface {
            id
            name
            barcode {
              value
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

type ParentStorableResolutionResult =
  | {parentStorableTypedValue: StorablesTypedValue; positionTypedValue?: GridCoordinatesTypedValue}
  | SourceStorableError;
interface SourceStorableError {
  errorCode: SourceStorableErrorCode;
}
enum SourceStorableErrorCode {
  INACCESSIBLE = 'INACCESSIBLE',
  INVALID_SCHEMA = 'INVALID_SCHEMA',
  INVALID_SCHEMA_TYPE = 'INVALID_SCHEMA_TYPE',
  HAS_MULTIPLE_CONTENTS = 'HAS_MULTIPLE_CONTENTS',
  HAS_BATCH = 'HAS_BATCH',
  OUT_OF_BOUNDS = 'OUT_OF_BOUNDS',
  INVALID_POSITION_FORMAT = 'INVALID_POSITION_FORMAT',
  INACCESSIBLE_PARENT = 'INACCESSIBLE_PARENT',
}

function isError(resolutionResult: ParentStorableResolutionResult): resolutionResult is SourceStorableError {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'errorCode' in resolutionResult;
}

// To mock our GraphQL call, we need to wrap it in a method.
// This is different from most of our GraphQL tests, where we mock the Apollo cache.
// Here, mocking the cache won't work, because our fetch policy is `'no-cache'`.
export const stubbableFunctions = {
  async doContainerParentStorableQuery(containerId: string): Promise<GetContainerParentStorable> {
    return await doQueryWithoutCache<GetContainerParentStorable, GetContainerParentStorableVariables>(
      {query: GET_CONTAINER_PARENT_STORABLE_QUERY},
      {variables: {containerIds: [containerId]}}
    );
  },

  async doPlateParentStorableQuery(plateId: string): Promise<GetPlateParentStorable> {
    return await doQueryWithoutCache<GetPlateParentStorable, GetPlateParentStorableVariables>(
      {query: GET_PLATE_PARENT_STORABLE_QUERY},
      {variables: {plateIds: [plateId]}}
    );
  },
};

/**
 * Do data fetching for resolving the Location (parent storable) and Position columns.
 *
 * If successful, return a StorablesTypedValue representing the container's location and
 * possibly a GridCoordinatesTypedValue representing the position (if in a box/plate).
 *
 * If there is an error, return the error code.
 */
async function resolveContainerParentStorable(
  containerId: string,
  containerSchemaId?: string | null
): Promise<ParentStorableResolutionResult> {
  const data = await stubbableFunctions.doContainerParentStorableQuery(containerId);

  // Validate container
  assert(data.containers.length === 1, 'Expected one container result from Container Parent Storable query');
  const container = data.containers[0];
  if (isInaccessible(container)) {
    return {errorCode: SourceStorableErrorCode.INACCESSIBLE};
  }
  if (containerSchemaId != null && containerSchemaId !== container.storableSchema.id) {
    return {errorCode: SourceStorableErrorCode.INVALID_SCHEMA};
  }

  const parentStorable = container.parentStorable;
  const gridCoordinates = container.gridCell;
  if (
    (parentStorable != null && isInaccessible(parentStorable)) ||
    (gridCoordinates != null && isInaccessible(gridCoordinates))
  ) {
    return {errorCode: SourceStorableErrorCode.INACCESSIBLE_PARENT};
  }

  return {
    parentStorableTypedValue: {
      type: BaseTypedValueType.STORABLES,
      storableSummaries:
        parentStorable != null
          ? [
              {
                id: parentStorable.id,
                name: parentStorable.name,
                barcode: parentStorable.barcode?.value,
              },
            ]
          : [],
    },
    ...(gridCoordinates != null && {
      positionTypedValue: {
        type: BaseTypedValueType.GRID_COORDINATES,
        gridCoordinates: {columnIndex: gridCoordinates.columnIndex, rowIndex: gridCoordinates.rowIndex},
      },
    }),
  };
}

/**
 * Do data fetching for resolving the Location (parent storable) of a plate.
 *
 * If successful, return a StorablesTypedValue representing the plate's location.
 *
 * If there is an error, return the error code.
 */
async function resolveFixedPlateParentStorable(
  plateId: string,
  plateSchemaId?: string | null
): Promise<ParentStorableResolutionResult> {
  const data = await stubbableFunctions.doPlateParentStorableQuery(plateId);

  // Validate plate
  const plate = data.plates[0];
  if (isInaccessible(plate)) {
    return {errorCode: SourceStorableErrorCode.INACCESSIBLE};
  }
  assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');
  if (plateSchemaId != null && plateSchemaId !== plate.storableSchema.id) {
    return {errorCode: SourceStorableErrorCode.INVALID_SCHEMA};
  }
  if (plate.storableSchema.plateSchemaType !== PlateSchemaType.FIXED_PLATE) {
    return {errorCode: SourceStorableErrorCode.INVALID_SCHEMA_TYPE};
  }

  const parentStorable = plate.parentStorable;
  if (parentStorable != null && isInaccessible(parentStorable)) {
    return {errorCode: SourceStorableErrorCode.INACCESSIBLE_PARENT};
  }

  return {
    parentStorableTypedValue: {
      type: BaseTypedValueType.STORABLES,
      storableSummaries:
        parentStorable != null
          ? [
              {
                id: parentStorable.id,
                name: parentStorable.name,
                barcode: parentStorable.barcode?.value,
              },
            ]
          : [],
    },
  };
}

export const ParentStorableResolver = {
  autopopulateContainerParentStorable: async (
    query: InventoryTableParentStorableFromContainerQuery
  ): Promise<ServerResolutionResult> => {
    const resolutionResult = await resolveContainerParentStorable(query.containerId, query.containerSchemaId);
    if (isError(resolutionResult)) {
      // The source container is invalid.
      // To fix the error, the user must fix the Source Container column.
      // For the Parent storable column, we shouldn't return an error. It wouldn't be actionable.
      return makeCustomResolutionResultFromTypedValue(null);
    } else {
      return makeCustomResolutionResultFromTypedValue(resolutionResult.parentStorableTypedValue);
    }
  },

  autopopulatePlateParentStorable: async (
    query: InventoryTableParentStorableFromPlateQuery
  ): Promise<ServerResolutionResult> => {
    const resolutionResult = await resolveFixedPlateParentStorable(query.plateId, query.plateSchemaId);
    if (isError(resolutionResult)) {
      // The source plate is invalid.
      // To fix the error, the user must fix the Source Plate column.
      // For the Parent storable column, we shouldn't return an error. It wouldn't be actionable.
      return makeCustomResolutionResultFromTypedValue(null);
    } else {
      return makeCustomResolutionResultFromTypedValue(resolutionResult.parentStorableTypedValue);
    }
  },

  autopopulateContainerGridPosition: async (
    query: InventoryTableGridPositionFromContainerQuery
  ): Promise<ServerResolutionResult> => {
    const resolutionResult = await resolveContainerParentStorable(query.containerId, query.containerSchemaId);
    if (isError(resolutionResult)) {
      // The source container is invalid.
      // To fix the error, the user must fix the Source Container column.
      // For the Position column, we shouldn't return an error. It wouldn't be actionable.
      return makeCustomResolutionResultFromTypedValue(null);
    } else {
      return makeCustomResolutionResultFromTypedValue(resolutionResult.positionTypedValue ?? null);
    }
  },

  validateParentStorableCapacity: async (
    query: InventoryValidateParentStorableCapacityQuery
  ): Promise<ServerResolutionResult> => {
    const {isValid, errorMessage} = await validateItemsForParentStorable(
      query.parentStorableId,
      [{storableSchema: {id: query.childSchemaId, name: query.childSchemaName}}],
      'no-cache'
    );

    if (!isValid && errorMessage != null) {
      return {
        type: undefined,
        error: errorMessage,
        resolvedValue: undefined,
      };
    }
    return makeCustomResolutionResultFromTypedValue(null);
  },
};

export const getContainerParentStorableCustomEval = lruMemoize(
  _getContainerParentStorableCustomEval,
  {max: 100},
  (...args) => stringify(args)
);

export const resetContainerParentStorableCustomEvalCache = (): void => {
  getContainerParentStorableCustomEval._cache.reset();
};

function _getContainerParentStorableCustomEval<CustomEvalOptions>(
  lockIfContainerValIsEmpty: boolean,
  containerSchemaId?: string | null
): CustomEval<CustomEvalOptions> {
  return (
    typedValue: TypedValue,
    columnDefinition: ParentStorableColumnDefinition,
    cellDependencies: [TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    const containerId = extractContainerId(cellDependencies[0]);
    if (!containerId) {
      return {
        val: {type: BaseTypedValueType.STRING, rawValue: ''},
        error: null,
        locked: lockIfContainerValIsEmpty,
      };
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_PARENT_STORABLE_FROM_CONTAINER,
        containerId,
        containerSchemaId: containerSchemaId ?? null,
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: resolvedValue, error, locked: true};
  };
}

export const getPlateParentStorableCustomEval = lruMemoize(
  _getPlateParentStorableCustomEval,
  {max: 100},
  (...args) => stringify(args)
);

function _getPlateParentStorableCustomEval<CustomEvalOptions>(
  lockIfPlateValIsEmpty: boolean,
  plateSchemaId?: string | null
): CustomEval<CustomEvalOptions> {
  return (
    typedValue: TypedValue,
    columnDefinition: ParentStorableColumnDefinition,
    cellDependencies: [TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    const plateId = extractPlateId(cellDependencies[0]);
    if (!plateId) {
      return {
        val: {type: BaseTypedValueType.STRING, rawValue: ''},
        error: null,
        locked: lockIfPlateValIsEmpty,
      };
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_PARENT_STORABLE_FROM_PLATE,
        plateId,
        plateSchemaId: plateSchemaId ?? null,
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: resolvedValue, error, locked: true};
  };
}

export const getContainerGridPositionCustomEval = lruMemoize(
  _getContainerGridPositionCustomEval,
  {max: 100},
  (...args) => stringify(args)
);

function _getContainerGridPositionCustomEval<CustomEvalOptions>(
  lockIfContainerValIsEmpty: boolean,
  containerSchemaId?: string | null
): CustomEval<CustomEvalOptions> {
  return (
    typedValue: TypedValue,
    columnDefinition: PositionColumnDefinition,
    cellDependencies: [TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    const containerId = extractContainerId(cellDependencies[0]);
    if (!containerId) {
      return {
        val: {type: BaseTypedValueType.STRING, rawValue: ''},
        error: null,
        locked: lockIfContainerValIsEmpty,
      };
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_GRID_POSITION_FROM_CONTAINER,
        containerId,
        containerSchemaId: containerSchemaId ?? null,
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: resolvedValue, error, locked: true};
  };
}

export const validateParentStorableCapacityCustomEval = lruMemoize(
  _validateParentStorableCapacityCustomEval,
  {max: 100},
  (...args) => stringify(args)
);

// childStorableSchema must be passed in for this to be used on plate or box columns, which do not have
// an attached child storable schema (unlike parent storable column)
function _validateParentStorableCapacityCustomEval<CustomEvalOptions>(childStorableSchema?: {
  id: string;
  name: string;
}): CustomEval<CustomEvalOptions> {
  return (
    parentTypedValue: TypedValue,
    columnDefinition: ParentStorableColumnDefinition | BoxColumnDefinition | PlateColumnDefinition,
    cellDependencies: [],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn,
    resolveTypedValueToType: ResolveTypedValueToTypeFn
  ) => {
    // TODO(BNCH-15744): customEval currently runs _before_ string resolution and this particular resolution
    // would like to operate on a post-resolved value, so we manually fire it off first.
    const {resolvedValue: resolvedParentTypedValue, error: resolutionError} = resolveTypedValueToType(
      columnDefinition,
      parentTypedValue
    );
    if (resolutionError || resolvedParentTypedValue == null) {
      return {val: resolvedParentTypedValue ?? parentTypedValue, error: resolutionError, locked: false};
    }
    const parentStorableId = extractStorableId(resolvedParentTypedValue);
    if (!parentStorableId) {
      return {val: resolvedParentTypedValue, error: null, locked: false};
    }
    const parentStorableIsExpectedType =
      columnDefinition.type === TableColumnType.BOX
        ? APIUtil.isBox(parentStorableId)
        : columnDefinition.type === TableColumnType.PLATE
          ? APIUtil.isPlate(parentStorableId)
          : APIUtil.isLocation(parentStorableId);
    if (!parentStorableIsExpectedType) {
      return {val: resolvedParentTypedValue, error: null, locked: false};
    }

    const childSchema =
      (columnDefinition as ParentStorableColumnDefinition).storableSchema ?? childStorableSchema;
    if (childSchema == null) {
      assertAndContinue(false, 'Expected child schema to be provided in order to validate parent capacity');
      return {val: resolvedParentTypedValue, error: null, locked: false};
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      resolvedParentTypedValue,
      {
        customResolutionQueryType: CustomResolutionQueryType.INVENTORY_VALIDATE_PARENT_STORABLE_CAPACITY,
        parentStorableId,
        childSchemaId: childSchema.id,
        childSchemaName: childSchema.name,
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: resolvedValue, error, locked: false};
  };
}
